import {inject, Injectable, NgZone} from '@angular/core';
import {DialogService} from '../dialog/dialog.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {BehaviorSubject, lastValueFrom, ObservableInput} from 'rxjs';
import {createBlobFromBase64} from '../../util/blob-from-base64.util';
import {AuthService} from '../auth/auth.service';
import {HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from '@angular/common/http';
import {catchError, map, tap} from 'rxjs/operators';
import {VaultsService as ApiVaultsService} from '../../api/services/vaults.service';
import {MagnetsService as ApiMagnetsService} from '../../api/services/magnets.service';
import {StrictHttpResponse} from 'src/app/api/strict-http-response';
import {UploadFile} from '../../models/import/upload-file.model';
import {StatusFile} from '../../models/status-file/status-file.model';
import {SameFileDetectedEnum} from '../../enums/same-file-detected.enum';
import {SameFileDetected} from '../../types/same-file-detected.type';
import {StatusFileUpload} from '../../models/status-file/status-file-upload.model';
import {FileUploadStatusEnum} from '../../enums/file-upload-status.enum';
import {ImportLocation} from '../../models/import/import-location.model';
import {Document} from '../../api/models/document';
import {CheckoutEventService} from '../electron/checkout/checkout-event.service';
import {DefaultIconIdsService as ApiDefaultIconIdsService} from '../../api/services/default-icon-ids.service';
import {DefaultFileExtensionIconIds} from '../../api/models/default-file-extension-icon-ids';
import {DocumentQuery} from '../../queries/document.query';
import {ApteraApiError} from '../../models/aptera-api-error.model';
import {ImportDialogResult} from '../../models/import/import-dialog-result.model';
import {VaultQuery} from '../../queries/vault.query';
import {AppComponentService} from '../app-component/app-component.service';
import {AppComponentApiIdsEnum} from '../../enums/app-component-api-ids.enum';
import {ServerSelectionService} from '../server-selection/server-selection.service';
import {ApiErrorCodeEnum} from '../../enums/api-error-code.enum';
import {MagnetService} from '../magnet/magnet.service';
import {Magnet} from '../../bfa-api/models/magnet';
import {ImportDialogData} from '../../models/import/import-dialog-data.model';
import {AppDialogModal} from '../../models/modal/dialog/app-dialog-modal.model';
import {ImportDialogModalComponent} from '../../components/dialogs/import-dialog-modal/import-dialog-modal.component';
import {ImportLocationEnum} from '../../enums/import-location.enum';
import {CheckedOutDocumentService} from '../checked-out-document/checked-out-document.service';

@Injectable({
    providedIn: 'root'
})
export class ImportService {
    public files$: BehaviorSubject<Array<UploadFile>> = new BehaviorSubject<Array<UploadFile>>([]);
    private readonly authService = inject(AuthService);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly ngZone = inject(NgZone);
    private readonly http = inject(HttpClient);
    private readonly apiVaultsService = inject(ApiVaultsService);
    private readonly apiMagnetService = inject(ApiMagnetsService);
    private readonly apiDefaultIconIds = inject(ApiDefaultIconIdsService);
    private readonly documentQuery = inject(DocumentQuery);
    private readonly checkoutEventService = inject(CheckoutEventService);
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly magnetService = inject(MagnetService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly appComponentService = inject(AppComponentService);
    private readonly checkedOutDocumentService = inject(CheckedOutDocumentService);
    private readonly numberMaxFiles = 10;
    private importDocumentId: string | undefined;
    private importDialog: AppDialogModal<ImportDialogModalComponent, ImportDialogData, ImportDialogResult> | undefined;

    public startImport(target: Magnet | ImportLocationEnum | undefined): void {
        const importTarget: Magnet | ImportLocation | undefined = typeof target === 'string' ? {
            id: target,
            name: target
        } : target;
        this.showImportDialog(undefined, undefined, undefined, importTarget);
        this.checkForCheckingInDocument()
            .then(importDocumentId => {
                if (importDocumentId) {
                    this.checkedOutDocumentService.checkInDocument(importDocumentId)
                        .then();
                }
            });
    }

    public importDialogOpen(): boolean {
        return this.importDialog !== undefined;
    }

    public hasFiles(): boolean {
        return this.files$.getValue().length > 0;
    }

    public addFiles(fileArray: Array<UploadFile>): boolean {
        if (fileArray.length > this.numberMaxFiles) {
            this.dialogService.showDialog({
                messageTranslationKey: 'IMPORT.ERROR_TO_MUCH_FILES',
                translateKeyParameters: { maxFiles: this.numberMaxFiles }
            }, 'error-too-many-files');
            return false;
        }

        this.files$.next(fileArray);
        return true;
    }

    public resetFiles(): void {
        this.files$.next([]);
    }

    public async checkForCheckingInDocument(): Promise<string | undefined> {
        const selectedDocument = this.documentQuery.getSelectedDocument();
        const importDialog = this.importDialog;
        if (!selectedDocument || !importDialog) {
            return undefined;
        }

        const importDialogResult = await new Promise<ImportDialogResult | undefined>(resolve => {
            importDialog.afterClosed(resolve);
        });
        const hasUploadedSelectedDocument = importDialogResult?.statusFiles
            .some((statusFile: StatusFile) => statusFile.upload()?.status !== FileUploadStatusEnum.Error && statusFile.file.name === selectedDocument.name) ?? false;
        if (!hasUploadedSelectedDocument) {
            return undefined;
        }

        const isConfirmed = await this.dialogService.showConfirmDialog({
            messageTranslationKey: 'IMPORT.POST_IN_PROGRESS_IMPORT',
            actionTranslationKey: 'IMPORT.POST_IN_PROGRESS_IMPORT_ACTION',
            cancelTranslationKey: 'IMPORT.POST_IN_PROGRESS_IMPORT_CANCEL'
        }, 'import-post-in-progress');
        if (!isConfirmed) {
            return undefined;
        }

        return this.importDocumentId || this.documentQuery.getSelectedDocumentId();
    }

    public showImportDialog(files?: Array<UploadFile>,
                            vaultId?: string,
                            magnetId?: string,
                            target?: Magnet | ImportLocation | undefined,
                            collisionSetting?: SameFileDetected,
                            documentId?: string,
                            showErrorToast: boolean = false,
                            shouldSuppressOpenAfterImport: boolean = false,
    ): AppDialogModal<ImportDialogModalComponent, ImportDialogData, ImportDialogResult> {
        if (this.importDialog === undefined) {
            this.importDocumentId = documentId;
            this.importDialog = this.dialogService.showImportDialog({
                files,
                vaultId,
                magnetId,
                target,
                collisionSetting,
                documentId
            });
            if (this.importDialog) {
                this.importDialog.afterClosed(async (ref: ImportDialogData | ImportDialogResult | undefined) => {
                    this.importDialog = undefined;
                    const importDialogResult = ref as ImportDialogResult;
                    if (!importDialogResult) {
                        return undefined;
                    }

                    let hasOneSuccessfulUpload = false;
                    let hasOneErrorUpload = false;
                    for (const statusFile of importDialogResult.statusFiles) {
                        if (!statusFile.upload()) {
                            console.error('Upload undefined in result');
                            continue;
                        }

                        const uploadResult = statusFile.upload();
                        switch (uploadResult?.status) {
                            case FileUploadStatusEnum.Done:
                                hasOneSuccessfulUpload = true;
                                if (uploadResult.documentId && importDialogResult.importLocation === 'InProgress' && !shouldSuppressOpenAfterImport) {
                                    this.checkoutEventService.openLocalDocumentEvent.emit(uploadResult.documentId);
                                }
                                break;
                            case FileUploadStatusEnum.Error:
                                hasOneErrorUpload = true;
                                break;
                            default:
                                console.error('undefined status for file upload');
                                break;
                        }
                    }

                    if (hasOneSuccessfulUpload) {
                        switch (importDialogResult.importLocation) {
                            case 'InProgress':
                                this.snackbarService.showInfo('IMPORT.FILE_PROCESSING_INFO');
                                this.appComponentService.getAppComponentApiById(AppComponentApiIdsEnum.IN_PROGRESS_LIST)
                                    .then((componentApi) => {
                                        componentApi.showListNeedsReload?.();
                                    });
                                break;
                            case 'Documents':
                                const vaultName = this.vaultQuery.getVaultNameById(importDialogResult.vaultId);
                                this.snackbarService.showInfo('IMPORT.SUCCESSFUL_IMPORT_TO_VAULT', { vaultName });
                                break;
                            default:
                                try {
                                    const magnet = await this.magnetService.fetchAndGetMagnet(importDialogResult.importLocation);
                                    if (magnet.magnetName) {
                                        this.snackbarService.showInfo('IMPORT.SUCCESSFUL_IMPORT_TO_MAGNET', { magnetName: magnet.magnetName });
                                        break;
                                    }
                                } catch {
                                    // No need to validate the error here. We simply show a generic message that the import was successful if the magnet cannot be retrieved here.
                                }

                                this.snackbarService.showInfo('IMPORT.IMPORTED');
                        }
                    }

                    if (hasOneErrorUpload && showErrorToast) {
                        this.snackbarService.showInfo('IMPORT.FILE_MOVED_INFO');
                    }
                });
            }
        } else {
            if (files) {
                this.files$.next(files);
            }
        }
        return this.importDialog;
    }

    public fileListToArray(fileList: FileList, files: Array<File> = []): Array<File> {
        if (fileList.length > 0) {
            for (let i = 0; i < fileList.length; i++) {
                files.push(fileList[i]);
            }
        }

        return files;
    }

    public addFromAndroid(fileDataArray: Array<{ name: string; file: string }>): void {
        let retValue = true;
        this.ngZone.run(() => {
            this.authService.continueAfterLoggedIn(async () => {
                if (this.importDialogOpen() && this.hasFiles()) {
                    this.snackbarService.showError('IMPORT.ALREADY_STARTED_CONFIRM');
                    retValue = false;
                }
                const fileFetches: Array<Promise<File>> = [];
                for (const fileData of fileDataArray) {
                    fileFetches.push(fetch(fileData.file)
                        .then(r => r.blob())
                        .then(b => new File([b], fileData.name)));
                }
                try {
                    this.showImportDialog(await Promise.all(fileFetches));
                    retValue = true;
                } catch (e) {
                    console.error(e, fileDataArray);
                    retValue = false;
                }
                return retValue;
            });
        });
    }

    public addFromIOS(fileDataArray: Array<{ name: string; file: string }>): void {
        this.ngZone.run(() => {
            this.authService.continueAfterLoggedIn((): Promise<boolean> => {
                if (this.importDialogOpen() && this.hasFiles()) {
                    this.snackbarService.showError('IMPORT.ALREADY_STARTED_CONFIRM');
                    return Promise.resolve(false);
                }
                const files: Array<File> = [];
                for (const fileData of fileDataArray) {
                    const file = new File([createBlobFromBase64(fileData.file)], fileData.name);
                    files.push(file);
                }
                this.showImportDialog(files);
                return Promise.resolve(true);
            });
        });
    }

    public uploadMagnetDocument(statusFile: StatusFile, magnetId: string, sameFileDetected: SameFileDetected): Promise<StatusFile> {
        return new Promise<StatusFile>((resolve) => {
            statusFile.upload.set({
                status: FileUploadStatusEnum.Waiting,
                progress: 0
            });
            const file = statusFile.file;
            this.authService.getBearer()
                .then(bearer => {
                    return lastValueFrom(this.apiMagnetService.MagnetsCreateDocumentResponse({
                        Authorization: bearer,
                        magnetId,
                        document: {
                            generateNonExistingNameIfNameExists: sameFileDetected === SameFileDetectedEnum.extend,
                            metadata: {
                                createDate: (new Date(file.creationDate || file.lastModified)).toUTCString(),
                                changeDate: (new Date(file.lastModified)).toUTCString(),
                                name: file.name,
                                size: file.size
                            }
                        }
                    }));
                })
                .then(async response => {
                    resolve(await this.documentUpload(statusFile, response, sameFileDetected));
                })
                .catch(e => {
                    const error = (e.error) ? e.error : e;
                    const uploadStatus: StatusFileUpload = {
                        status: FileUploadStatusEnum.Error,
                        progress: 0,
                        error: error,
                        documentId: error.details?.duplicateDocumentId,
                        filePath: file.filePath,
                    };
                    statusFile.upload.set(uploadStatus);
                    resolve(statusFile);
                });
        });
    }

    public uploadNewCheckedOutDocument(statusFile: StatusFile, vaultId: string, sameFileDetected: SameFileDetected): Promise<StatusFile> {
        return new Promise<StatusFile>((resolve) => {
            statusFile.upload.set({
                status: FileUploadStatusEnum.Waiting,
                progress: 0
            });
            const file = statusFile.file;
            this.authService.getBearer()
                .then(bearer => {
                    return lastValueFrom(this.apiVaultsService.VaultsPostCheckedOutDocumentResponse({
                        Authorization: bearer,
                        vaultId,
                        documentCreationData: {
                            generateNonExistingNameIfNameExists: sameFileDetected === SameFileDetectedEnum.extend,
                            metadata: {
                                createDate: (new Date(file.creationDate || file.lastModified)).toUTCString(),
                                changeDate: (new Date(file.lastModified)).toUTCString(),
                                name: file.name,
                                size: file.size
                            }
                        }
                    }));
                })
                .then(async response => {
                    resolve(await this.documentUpload(statusFile, response, sameFileDetected));
                })
                .catch(e => {
                    const error = (e.error) ? e.error : e;
                    const uploadStatus: StatusFileUpload = {
                        status: FileUploadStatusEnum.Error,
                        progress: 0,
                        error: error,
                        documentId: error.details?.duplicateDocumentId,
                        filePath: file.filePath,
                    };

                    statusFile.upload.set(uploadStatus);
                    resolve(statusFile);
                });
        });
    }

    public uploadVaultDocument(statusFile: StatusFile, vaultId: string, sameFileDetected: SameFileDetected): Promise<StatusFile> {
        return new Promise<StatusFile>((resolve) => {
            statusFile.upload.set({
                status: FileUploadStatusEnum.Waiting,
                progress: 0
            });
            const file = statusFile.file;
            this.authService.getBearer()
                .then(bearer => {
                    return lastValueFrom(this.apiVaultsService.VaultsPostDocumentResponse({
                        Authorization: bearer,
                        vaultId,
                        document: {
                            generateNonExistingNameIfNameExists: sameFileDetected === SameFileDetectedEnum.extend,
                            metadata: {
                                createDate: (new Date(file.creationDate || file.lastModified)).toUTCString(),
                                changeDate: (new Date(file.lastModified)).toUTCString(),
                                name: file.name,
                                size: file.size
                            }
                        }
                    }));
                })
                .then(async response => {
                    resolve(await this.documentUpload(statusFile, response, sameFileDetected));
                })
                .catch(async vaultDocumentError => {
                    const error = vaultDocumentError.error ?? vaultDocumentError;

                    if (sameFileDetected === SameFileDetectedEnum.overwrite && error.code === ApiErrorCodeEnum.NameExists) {
                        console.error(error);
                        resolve(await this.documentUpload(statusFile, new HttpResponse({ body: error }), sameFileDetected));
                        return;
                    }

                    const uploadStatus: StatusFileUpload = {
                        status: FileUploadStatusEnum.Error,
                        progress: 0,
                        error: error,
                        documentId: error.details?.duplicateDocumentId,
                        filePath: file.filePath,
                    };

                    statusFile.upload.set(uploadStatus);
                    resolve(statusFile);
                });
        });
    }

    public async uploadExistingCheckedOutDocument(
        document: Document,
        statusFile: StatusFile,
    ): Promise<StatusFile> {
        const data = new FormData();
        data.append('file', statusFile.file);
        const serverUrl = this.serverSelectionService.getInternalApiUrl();
        const url = `${serverUrl}/checked-out-documents/${encodeURIComponent(document.id)}/file`;

        return lastValueFrom(this.http.put(url, data, {
                responseType: 'json',
                headers: {
                    Authorization: await this.authService.getBearer(),
                    'Amagno-File-Create-Date': (new Date(document.createDate)).toISOString(),
                    'Amagno-File-Change-Date': (new Date(statusFile.file.lastModified)).toISOString(),
                },
                reportUploadProgress: true,
                reportDownloadProgress: true,
                observe: 'events',
            })
            .pipe(
                map(r => r as HttpEvent<StatusFileUpload>),
                map(this.handleUploadStatus),
                catchError((err: HttpErrorResponse): ObservableInput<StatusFileUpload> => {
                    return Promise.resolve({
                        status: FileUploadStatusEnum.Error,
                        progress: 0,
                        error: {
                            code: err.error?.code || '0',
                            message: err.error?.message || err.message
                        },
                        documentId: document.id,
                        filePath: statusFile.file.filePath,
                    });
                }),
                map((statusFileUploadData: StatusFileUpload) => {
                    statusFileUploadData.filePath = statusFile.file.filePath;
                    if (statusFile.upload()) {
                        statusFile.upload.set(statusFileUploadData);
                    }
                    return statusFile;
                })
            ));
    }

    public async fetchAllFileExtensionIconIds(): Promise<DefaultFileExtensionIconIds> {
        return lastValueFrom(this.apiDefaultIconIds.DefaultIconIdsGetFileExtensions(await this.authService.getBearer()));
    }

    private async documentUpload(statusFile: StatusFile, response: StrictHttpResponse<unknown>, sameFileDetected: SameFileDetected): Promise<StatusFile> {
        return new Promise(async (resolveInner) => {
            const file = statusFile.file;
            let documentId = '';
            if (response) {
                if (response.body && typeof response.body === 'object' && 'code' in response.body && response.body.code) {
                    if (sameFileDetected !== SameFileDetectedEnum.overwrite || response.body.code !== ApiErrorCodeEnum.NameExists && !('details' in response.body)) {
                        const uploadStatus: StatusFileUpload = {
                            status: FileUploadStatusEnum.Error,
                            progress: 0,
                            error: response.body as ApteraApiError,
                            documentId,
                            filePath: statusFile.file.filePath,
                        };

                        statusFile.upload.set(uploadStatus);
                        resolveInner(statusFile);
                        return;
                    } else {
                        if ('details' in response.body && response.body.details && typeof response.body.details === 'object'
                            && 'duplicateDocumentId' in response.body.details && typeof response.body.details.duplicateDocumentId === 'string') {
                            documentId = response.body.details.duplicateDocumentId;
                        }
                    }
                } else {
                    const location = response.headers.get('location') || response.headers.get('Location');
                    if (!location) {
                        const uploadStatus: StatusFileUpload = {
                            status: FileUploadStatusEnum.Error,
                            progress: 0,
                            error: {
                                code: 0,
                                message: 'missing location header in response'
                            },
                            filePath: statusFile.file.filePath,
                        };

                        statusFile.upload.set(uploadStatus);
                        resolveInner(statusFile);
                        return;
                    }
                    const locationParts = location.split('/');
                    documentId = locationParts[locationParts.length - 1];
                }


                const data = new FormData();
                data.append('file', file);
                const serverUrl = this.serverSelectionService.getInternalApiUrl();
                const url = `${serverUrl}/documents/${encodeURIComponent(documentId)}/file`;

                let responseData;
                const bearer = await this.authService.getBearer();
                try {
                    responseData = await lastValueFrom(this.http.put(url, data, {
                            responseType: 'json',
                            headers: {
                                Authorization: bearer,
                                'Amagno-File-Create-Date': (new Date(file.creationDate || file.lastModified)).toISOString(),
                                'Amagno-File-Change-Date': (new Date(file.lastModified)).toISOString(),
                            },
                            reportUploadProgress: true,
                            reportDownloadProgress: true,
                            observe: 'events',
                        })
                        .pipe(
                            map(r => r as HttpEvent<StatusFileUpload>),
                            map(this.handleUploadStatus),
                            tap((statusFileUploadData: StatusFileUpload) => {
                                if (!('documentId' in statusFileUploadData)) {
                                    statusFileUploadData.documentId = documentId;
                                }
                                statusFileUploadData.filePath = statusFile.file.filePath;
                                statusFile.upload.set(statusFileUploadData);
                                if (statusFileUploadData.status === FileUploadStatusEnum.Error) {
                                    resolveInner(statusFile);
                                }
                            })
                        ));
                } catch (error) {
                    const httpError = error as HttpErrorResponse;
                    const uploadStatus: StatusFileUpload = {
                        status: FileUploadStatusEnum.Error,
                        progress: 0,
                        error: {
                            code: httpError.error.code,
                            message: httpError.error.message
                        },
                        documentId,
                        filePath: statusFile.file.filePath,
                    };

                    statusFile.upload.set(uploadStatus);
                }

                if (responseData) {
                    statusFile.upload.set(responseData);
                }

                resolveInner(statusFile);
            }
        });
    }

    private handleUploadStatus(httpEvent: HttpEvent<StatusFileUpload>): StatusFileUpload {
        const event = httpEvent as HttpProgressEvent;
        const status: StatusFileUpload = {
            progress: 0,
            status: FileUploadStatusEnum.Error,
        };

        switch (httpEvent.type) {
            case HttpEventType.Sent:
                status.status = FileUploadStatusEnum.Waiting;
                break;

            case HttpEventType.UploadProgress:
                if (event.total) {
                    status.status = FileUploadStatusEnum.Uploading;
                    status.progress = Math.round(100 * event.loaded / event.total);
                }
                break;

            case HttpEventType.ResponseHeader:
                status.status = FileUploadStatusEnum.Downloading;
                status.progress = 0;
                break;

            case HttpEventType.DownloadProgress:
                if (event.total) {
                    status.status = FileUploadStatusEnum.Downloading;
                    status.progress = Math.round(100 * event.loaded / event.total);
                }
                break;

            case HttpEventType.Response:
                status.status = FileUploadStatusEnum.Done;
                status.progress = 0;
                break;

            default:
                status.progress = 0;
                status.error = {
                    code: 0,
                    message: ''
                };
                status.status = FileUploadStatusEnum.Error;
        }
        return status;
    }
}
